import React from 'react'
import { useDispatch } from 'react-redux'
import { useSearchParams } from 'react-router'
import { dayjs } from '@mobi/utils'
import { useAppSelector } from '@core/Store/hooks'
import { useMeetingsQuery } from '@core/Areas/Racing/Hooks/useMeetings/useMeetingsQuery'
import type { RaceCodesLowerCase } from '@core/Areas/Racing/Types/MeetingInformation'
import { setMeetingsRaceCode } from '../Store'

export const useMeetingsData = () => {
  const dispatch = useDispatch()
  const raceCode = useAppSelector(rootState => rootState.meetings.currentCodeTab)

  const [searchParams, setSearchParams] = useSearchParams({
    date: dayjs(Date.now()).utcOffset('+0800').format('YYYY-MM-DD'),
  })

  const dateParam = searchParams.get('date')
  const currentDate = cleanDate(dateParam)

  // Clean date param if not valid
  React.useEffect(() => {
    if (dateParam !== currentDate) {
      setSearchParams(
        prevParams => {
          const newParams = new URLSearchParams(prevParams)
          newParams.set('date', currentDate)
          return newParams
        },
        { replace: true }
      )
    }
  }, [dateParam, currentDate, setSearchParams])

  const entryCode = searchParams.get('code')

  // Capture code search param
  React.useEffect(() => {
    if (!entryCode) return
    dispatch(setMeetingsRaceCode(cleanRaceCode(entryCode)))
    setSearchParams(
      prevParams => {
        const newParams = new URLSearchParams(prevParams)
        newParams.delete('code')
        return newParams
      },
      { replace: true }
    )
  }, [entryCode, dispatch, setSearchParams])

  const { isLoading, isError, data, refetch } = useMeetingsQuery(currentDate)

  const meetingsData = data ? data[currentDate] : undefined

  const setDate = React.useCallback(
    (value: Date | string) => {
      setSearchParams(prevParams => {
        const newParams = new URLSearchParams(prevParams)

        newParams.set('date', typeof value === 'string' ? value : cleanDate(value))
        return newParams
      })
    },
    [setSearchParams]
  )

  return {
    data: {
      isLoading,
      isError,
      refetch,
      dates: meetingsData?.dates,
      meetings: meetingsData?.meetings,
      fixedProducts: meetingsData?.fixedProducts,
    },
    handlers: {
      setDate,
    },
    state: {
      raceCode,
      currentDate,
    },
  }
}

// =============
// Local Helpers
// =============

const cleanDate = (date: string | Date | null): string =>
  !date || !dayjs(date).isValid()
    ? dayjs(Date.now()).utcOffset('+0800').format('YYYY-MM-DD')
    : dayjs(date).format('YYYY-MM-DD')

const cleanRaceCode = (code: string): RaceCodesLowerCase => {
  const validCodes: RaceCodesLowerCase[] = ['races', 'trots', 'dogs']
  return validCodes.includes(code?.toLowerCase() as unknown as RaceCodesLowerCase)
    ? (code as RaceCodesLowerCase)
    : 'races'
}
